import * as React from 'react'

import { Box, Button, Row, Title } from '@factorialco/deprecated-design-system'
import sortBy from 'lodash/sortBy'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import YearNavigation from 'modules/timeoff/components/YearNavigation'
import { locationHolidays } from 'modules/timeoff/store/collections/companyHolidays'
import type CompanyHoliday from 'modules/timeoff/store/models/CompanyHoliday'

import Holiday from './_Holiday'

interface Props {
  locationId: string
}

const LocationHolidays = ({ locationId }: Props) => {
  const { navigation } = useNavigation()

  const [year, setYear] = React.useState(moment().year())
  const holidaysOfLocation = locationHolidays(locationId)

  const filterByYear = (holiday: CompanyHoliday) => {
    const date = stringToDate(holiday.attributes.date, 'backend')

    return date.year() === year
  }

  const renderYearSelector = () => {
    const years = holidaysOfLocation.map((holiday) =>
      stringToDate(holiday.get('date'), 'backend').year()
    )

    const prevYear = moment().year() - 1
    const nextYear = moment().year() + 1
    const minYear = Math.min(...years, prevYear)
    const maxYear = Math.max(...years, nextYear)

    return (
      <Box
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
          bottom: {
            width: 's1',
            color: 'grey400',
            style: 'hidden',
          },
        }}
        paddingY="s16"
        borderRadius={{
          topLeft: 'abs010',
          topRight: 'abs010',
        }}
        background="grey200"
      >
        <YearNavigation
          type="terciary"
          year={year}
          minYear={minYear}
          maxYear={maxYear}
          onChange={setYear}
        />
      </Box>
    )
  }

  const renderBlankSlate = () => {
    return (
      <Box padding="s24" background="grey000White" textAlign="center">
        {i18n.t('settings.locations.location.holidays.no_holiday')}
      </Box>
    )
  }

  const renderHolidays = () => {
    const holidays = holidaysOfLocation.filter(filterByYear)

    if (!holidays.length) return renderBlankSlate()

    return sortBy(holidays, (holiday) => holiday.attributes.date).map((holiday, index) => (
      <Holiday key={`holiday_${index}`} holiday={holiday} locationId={locationId} />
    ))
  }

  const Holidays = () => (
    <Box paddingTop="s40" maxWidth="fit-content">
      {renderYearSelector()}
      <Box
        overflowX="hidden"
        borderRadius={{
          bottomLeft: 'abs010',
          bottomRight: 'abs010',
        }}
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
          top: {
            color: 'grey400',
            width: 's1',
            style: 'hidden',
          },
        }}
      >
        {renderHolidays()}
      </Box>
    </Box>
  )

  return (
    <Box flexGrow width="full" paddingX="s64" paddingY="s32">
      <Row>
        <Row.Col halfWidth>
          <Box paddingRight="s64">
            <Title
              variant="500"
              illustration="holidays-event-date"
              title={i18n.t('settings.locations.location.holidays.title')}
              description={i18n.t('settings.locations.location.holidays.description')}
            />
            <Box flexDirection="row" gap="s16" paddingTop="s16">
              <Button.Router
                to={navigation.settings.locations.locationsId.holidays.import.fullPath({
                  id: locationId,
                })}
                extra={{ 'data-intercom-target': 'import-holidays-button' }}
              >
                {i18n.t('settings.locations.location.holidays.import_holidays')}
              </Button.Router>
              <Button.Router
                to={navigation.settings.locations.locationsId.holidays.add.fullPath({
                  id: locationId,
                })}
                extra={{ 'data-intercom-target': 'add-custom-holidays-button' }}
              >
                {i18n.t('settings.locations.location.holidays.add_custom_holidays')}
              </Button.Router>
            </Box>
          </Box>
        </Row.Col>
        <Row.Col halfWidth>{!!holidaysOfLocation.length && <Holidays />}</Row.Col>
      </Row>
    </Box>
  )
}

export default observer(LocationHolidays)
